import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

interface Row {
  k: ReactNode;
  v: ReactNode;
}

function Table({ rows }: { rows: Row[] }) {
  return (
    <div className="dp-fields-table" style={{ marginTop: 18 }}>
      {rows.map((row, i) => (
        <div className="dp-field-row" key={i}>
          <div className="dp-field-k">{row.k}</div>
          <div className="dp-field-v">{row.v}</div>
        </div>
      ))}
    </div>
  );
}

const code = (text: string) => <span className="api-inline">{text}</span>;

/** The Finance tabs, in the order they appear. */
const TABS: Row[] = [
  { k: 'All Invoices', v: 'Every invoice, sortable, with a status filter. The filter includes Awaiting Usage for held drafts.' },
  { k: 'Past Due', v: 'One row per client with overdue invoices: total overdue, invoice count and days overdue. Over 30 days is Critical, over 7 is Warning, anything newer is Recent. Remind sends a reminder for each overdue invoice that client has (up to 100). View opens the client.' },
  { k: 'This Week', v: 'Invoices expected to be paid this week, with a Mark Paid action on each.' },
  { k: 'Awaiting', v: 'Invoices held as drafts because a metered reading is missing or the period is not sealed. They have no number or due date yet and issue by themselves once the reading is captured or waived. Waiting turns red after 7 days.' },
  { k: 'Expected Collections', v: 'Expected cash by date. Selecting a row drills into All Invoices for that week.' },
  { k: 'By Week', v: 'Amount due per week, with the invoice count and each week as a share of the total, plus the average, highest and lowest week.' },
  { k: 'Discrepancies', v: 'The same invoice arrived from two sources with different values. Resolving them is covered in the Merge, delete and undo guide.' },
  { k: 'Unmatched', v: 'Imported invoices not tied to a subscription. Select one row, then Link to Subscription or Create Subscription.' },
  { k: 'Tax', v: 'Tax collected, kept apart from revenue. Shown only when the workspace has the Tax add-on.' },
];

/** What the invoice detail rail offers, by status. */
const RAIL: Row[] = [
  { k: 'Pending', v: 'Send invoice (or Send reminder once an email has gone out), Mark as paid, Edit invoice, Cancel invoice, Regenerate invoice.' },
  { k: 'Overdue', v: 'Send overdue notice, Mark as paid, Adjust terms, Write off, Regenerate invoice.' },
  { k: 'Failed', v: 'Mark as paid, Retry / request payment, Edit invoice, Cancel invoice.' },
  { k: 'Paid', v: 'Edit invoice. This is where you reopen it, see section 08.' },
  { k: 'Cancelled', v: 'Edit invoice for notes and files only. Cancelled is final.' },
  { k: 'Any issued invoice', v: 'Issue credit note, added at the bottom. See the Credit notes guide.' },
];

export function CollectPaymentsGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Collect what you are owed — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Collect what you are owed</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Collect what <em>you are owed</em>.
        </h1>
        <p className="dp-oneliner">
          The Finance area shows what clients owe, when it should arrive and which invoices need a
          nudge. This guide covers reading it, sending invoices and reminders, recording payments
          (full or partial), undoing a payment recorded by mistake, and sending from a shared
          billing mailbox.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Who can do this, and what it needs
        </h2>
        <p className="dp-plain">
          Everything here needs the <strong>Finance</strong> feature on your workspace. Recording
          payments, editing invoices, sending invoices and reminders, and connecting the accounting
          mailbox are billing operations: you must be an admin of the workspace, not only a member.
          Credit notes also need Finance and are covered in{' '}
          <Link to="/docs/guides/credit-notes">their own guide</Link>.
        </p>
        <UiPath
          steps={['Finance']}
          note="The page is titled Invoices & Billing. Use New invoice to raise one by hand and Import for historical invoices."
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Read the receivables cards
        </h2>
        <p className="dp-plain">
          Four cards sit at the top: <strong>Total Receivables</strong>,{' '}
          <strong>Pending Payments</strong>, <strong>Overdue Amount</strong> and{' '}
          <strong>This Week</strong>. A red banner appears when anything is overdue and a second one
          when money is expected this week.
        </p>
        <Table
          rows={[
            { k: 'Reporting totals / By currency', v: 'The first button is named after your reporting currency, for example USD totals, and converts everything into it. By currency lists each invoice currency separately and never adds them together.' },
            { k: 'Gross / Net / Tax only', v: 'Appears once any receivable carries tax. It switches the card figures between the full amount, the amount before tax and the tax alone.' },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Read the cash-flow chart
        </h2>
        <p className="dp-plain">
          Under the cards is a 13-week line: the four weeks before this one, this week (marked
          Today) and the next eight. Weeks start on Monday. Past weeks show what was due and is
          still unpaid; future weeks show what Revvly expects to collect. Below it sit{' '}
          <strong>Outstanding</strong>, <strong>Past due</strong> and{' '}
          <strong>Collected (30d)</strong>.
        </p>
        <Table
          rows={[
            { k: 'Mouse', v: 'Move over the chart. A panel shows the week, the total per currency and the invoice count.' },
            { k: 'Touch or pen', v: 'Tap the chart, since there is no hover. Tap another point to move the panel.' },
            { k: 'Keyboard', v: 'Tab to the chart, then use the left and right arrows to step week by week, Home and End to jump to the first and last week, and Escape to close the panel.' },
            { k: 'The panel', v: 'Lists up to 6 invoices by size, each linking to the invoice, then "+N more". Past weeks are labelled Due (unpaid), future weeks Expected.' },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Work the tabs
        </h2>
        <p className="dp-plain">
          Badges on Awaiting, Discrepancies and Unmatched show how many items are waiting. The
          dashboard also links to <span className="api-inline">/finance?tab=awaiting</span>.
        </p>
        <Table rows={TABS} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          Send an invoice or a reminder
        </h2>
        <p className="dp-plain">
          Open the invoice and use the first button in <strong>Next steps</strong>. It reads{' '}
          <strong>Send invoice</strong> until an email has gone out for that invoice, then{' '}
          <strong>Send reminder</strong>, or <strong>Send overdue notice</strong> when it is past
          due. A panel opens with the recipients, subject and message filled in from a template.
        </p>
        <UiPath
          steps={['Finance', 'open an invoice', 'Next steps', 'Send invoice']}
          note="Revvly will not send a reminder for an invoice that is already paid."
        />
        <Table
          rows={[
            { k: 'Template', v: 'First send: Invoice or Custom. After that: Friendly nudge, Overdue notice, Final notice or Custom. Wording is editable under Library, Templates.' },
            { k: 'To and Cc', v: 'Pick from the client’s contacts that have an email address. At least one To address, a subject and a message are required.' },
            { k: 'Send from', v: 'Revvly, My Gmail (if you connected one) or Accounting mailbox (if your workspace has one). See section 11.' },
            { k: 'Attach invoice PDF', v: 'On by default when the invoice has files. If the PDF cannot be loaded, a mailbox send stops and tells you rather than sending without it.' },
            { k: 'Track opens', v: 'Records open events in the client conversation.' },
            { k: 'Reply-To', v: <>When Revvly sends, replies go to the Email in Settings, Invoice, or the workspace contact email if that is empty. A send from a Gmail mailbox comes from that mailbox, so replies go there.</> },
            { k: 'Warnings', v: 'The panel tells you how many days overdue the invoice is, and warns when the invoice is not due for at least 5 days and a reminder has already gone out.' },
            { k: 'Failures', v: 'A send the provider did not deliver to every To and Cc address is reported as a failure with the reason, not shown as sent. Each attempt, successful or not, is recorded against the invoice.' },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Payment terms and automatic reminders
        </h2>
        <p className="dp-plain">
          The due date is the issue date plus the payment terms. Set a default under Settings,
          General (a number of days, 30 unless you change it, or <strong>Due on receipt</strong>)
          and override it per client. Due on receipt is zero days: the due date is the issue date.
          On New invoice the Payment terms field starts from the client override, then the
          workspace default, and you can change it for that one invoice.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          Every day at 09:00 server time Revvly emails unpaid invoices (Pending or Overdue) whose
          due date is exactly 7 days away, today, or 1, 7, 14 or 30 days past. It skips a stage
          already sent in the last day and any client with no email address. Turn this off with{' '}
          <strong>Automatically send emails</strong>; while it is off the daily run sends nothing
          for your workspace and you send reminders yourself. The default is on.
        </p>
        <UiPath steps={['Settings', 'General', 'Automatically send emails']} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">07</span>
          Record a payment, in full or in part
        </h2>
        <p className="dp-plain">
          Use <strong>Mark as paid</strong> (also on Finance, This Week and the All Invoices row
          menu). The panel is titled <strong>Record a payment</strong> and asks for{' '}
          <strong>Amount received</strong>, prefilled with the outstanding balance, and the payment
          date.
        </p>
        <Table
          rows={[
            { k: 'Full amount', v: 'The invoice becomes Paid and drops out of receivables.' },
            { k: 'Less than the balance', v: 'The button reads Record part payment. The invoice stays Pending or Overdue with the remainder outstanding, so it keeps appearing in receivables and in reminders.' },
            { k: 'More than the balance', v: 'Recorded in full and the invoice settles. The panel states the overpayment.' },
            { k: 'Payment date', v: 'Set it to the day the money arrived, not the day you are typing.' },
            { k: 'Repeat payments', v: 'Each payment is added to the invoice’s payment list. Payments pushed in through the payment webhook land in the same list.' },
          ]}
        />
        <p className="dp-plain" style={{ marginTop: 18 }}>
          To have your bank or billing system post payments instead, see{' '}
          <Link to="/docs/guides/webhook-payment">Push payments in from your system</Link>.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">08</span>
          Reopen a paid invoice
        </h2>
        <p className="dp-plain">
          Paid is reversible, for a bounced transfer or a payment matched to the wrong invoice.
          Open the invoice, choose <strong>Edit invoice</strong> and untick{' '}
          <strong>Mark as paid</strong>. The panel warns that saving reopens the invoice as Pending
          or Overdue, depending on the due date.
        </p>
        <Figure
          title="Payment and reopening"
          caption="A part payment leaves the rest outstanding, and Paid can be reopened by editing the invoice."
        >
          <Flow
            steps={[
              {
                title: 'Pending or Overdue',
                text: 'Unpaid. Appears in receivables and reminders.',
                tone: 'warn',
              },
              {
                fork: [
                  {
                    title: 'Full amount',
                    text: 'The invoice becomes Paid and leaves receivables.',
                    tone: 'accent',
                  },
                  {
                    title: 'Part payment',
                    text: 'The remainder stays outstanding on the same invoice.',
                    tone: 'muted',
                  },
                ],
              },
              {
                title: 'Edit invoice',
                text: 'Untick Mark as paid on a Paid invoice.',
              },
              {
                title: 'Reopened',
                text: 'Back to Pending or Overdue, by due date.',
                tone: 'warn',
              },
            ]}
          />
        </Figure>
        <Table
          rows={[
            { k: 'What is reversed', v: 'The payment date is cleared. The collected amount goes back to outstanding, recorded as a reversing entry on the payment list rather than by deleting the original. The expected payment date falls back to the due date if it was empty. The invoice moves from collected cash back into the expected collections.' },
            { k: 'What stays locked', v: 'On a paid invoice the invoice number, issue date and line items cannot be changed. Status, payment dates and notes can. To correct the amounts, issue a credit note.' },
            { k: 'Replays', v: 'The original payment’s idempotency key is kept, so a webhook that retries the payment you reversed does not apply it again.' },
            { k: 'What is not allowed', v: 'Paid cannot go straight to Cancelled or Failed. Cancelled is final for every invoice.' },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">09</span>
          Zero-total invoices settle themselves
        </h2>
        <p className="dp-plain">
          An invoice that totals exactly zero, such as a fully discounted renewal, a free first
          period or a usage period with no usage, has nothing to collect. Revvly marks it Paid at
          its invoice date instead of letting it go overdue and trigger reminders. This applies
          when the invoice is created, when its PDF is recomputed and when you edit its line items.
          Only Pending and Overdue invoices are settled this way. A negative total (a credit note)
          and an invoice you cancelled or marked Failed are left alone, and a zero-total paid
          invoice stays editable.
        </p>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">10</span>
          The invoice detail action rail
        </h2>
        <p className="dp-plain">
          Next steps changes with the invoice status. The page has three tabs: Preview, Details
          (including Invoice files and the PDF) and Activity.
        </p>
        <Table rows={RAIL} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">11</span>
          Send from a shared accounting mailbox
        </h2>
        <p className="dp-plain">
          The accounting mailbox is one Gmail account owned by no user, such as
          billing@yourcompany.com. Anyone who can send an invoice can pick it as the sender, and it
          signs off with its own signature.
        </p>
        <UiPath
          steps={['Settings', 'Integrations', 'Accounting mailbox', 'Connect Gmail account']}
          note="Connecting, editing the signature and disconnecting are admin only. The card says so if email is not configured for your environment."
        />
        <Table
          rows={[
            { k: 'Signature', v: 'Add signature or Edit signature writes one. Import from Gmail replaces it with the signature set in that Gmail account. In the send panel an Accounting signature switch turns it off for one email.' },
            { k: 'Disconnect', v: 'Nobody can send from it until it is connected again. The signature is kept. A checkbox also deletes the emails the mailbox sent or received from Revvly; leave it off to keep them on client records.' },
            { k: 'The guard', v: <>Sending as the accounting mailbox is the one deliberate use of the shared address. A send made as a person must go out from that person’s own Gmail. If they have none it fails with {code('No connected mailbox for the sending user. Connect Gmail in your profile to send from it.')} so a colleague’s client email can never leave under the billing address.</> },
          ]}
        />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">12</span>
          Invoice header and payment options
        </h2>
        <p className="dp-plain">
          Your name, address and logo on every invoice come from Settings, Invoice, and your bank
          details from the Payment Information block in the same place. Both are covered in{' '}
          <Link to="/docs/guides/customize-invoices">Customize your invoices</Link>.
        </p>
        <p className="dp-plain" style={{ marginTop: 18 }}>
          To offer a different way to pay on one invoice only, open it, go to the Details tab and
          choose <strong>Add payment option</strong> (it reads Payment options with a count once
          some exist). Each option takes bank name, account name, account number, IBAN, SWIFT / BIC,
          routing / sort code, branch code and bank address, and is printed on that invoice. Credit
          notes ask the client for nothing, so they have no payment options.
        </p>
        <ul className="dp-connects" style={{ marginTop: 18 }}>
          <li>
            <Link to="/docs/guides/credit-notes" style={{ color: 'inherit', textDecoration: 'none' }}>
              Give money back with a credit note ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/fix-your-data" style={{ color: 'inherit', textDecoration: 'none' }}>
              Merge, delete and undo ›
            </Link>
          </li>
          <li>
            <Link to="/docs/concepts/invoice" style={{ color: 'inherit', textDecoration: 'none' }}>
              Invoice concept ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
